import { useCallback, useEffect, useState } from 'react'
import { Icon } from './Icon'
import { Confirm } from './Confirm'
import { sso, ROLE_LABELS, type SsoSettings, type SsoConfiguration } from '../api'

/**
 * Signing in through the customer's own directory.
 *
 * Eleven routes, two tables and a whole client shipped for this — and no
 * screen: the seven methods on `sso` in `api.ts` were the only ones in the
 * file that nothing called. A workspace could be signed in by Okta or Entra
 * only if somebody configured it with `curl`, which is not a feature that
 * shipped, it is a feature that was written down.
 *
 * The shape is the one the Domain section already uses, because it is the same
 * problem twice: something the customer has to set up in a system we do not
 * control, proved by a record they publish. Paste what their directory gives
 * them; we turn it into the three fields rather than asking them to find each
 * one. Then the email domains, each with the record to publish and whether it
 * has been seen.
 */
export function ConnectedDirectory({ mayChange }: { mayChange: boolean }) {
  const [state, setState] = useState<SsoSettings | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [disconnecting, setDisconnecting] = useState(false)
  const [dropping, setDropping] = useState<string | null>(null)

  /** The paste box, and what we read out of it. */
  const [metadata, setMetadata] = useState('')
  const [draft, setDraft] = useState<SsoConfiguration>({
    protocol: 'saml',
    issuer: '',
    label: '',
    defaultRole: 'editor',
  })
  const [domain, setDomain] = useState('')

  const load = useCallback(async () => {
    try {
      setState(await sso.settings())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void load(), [load])

  /** One act, with its own busy key and its own result. See `Card` in Settings. */
  const act = useCallback(
    async (name: string, work: () => Promise<unknown>, done: string) => {
      setBusy(name)
      setError(null)
      setNote(null)
      try {
        await work()
        await load()
        setNote(done)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(null)
      }
    },
    [load],
  )

  if (!state) {
    return (
      <section className="settings__card" data-sso-card>
        <h2>Your own directory</h2>
        {error ? (
          <p className="card__failed">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <span className="skeleton skeleton--line" />
        )}
      </section>
    )
  }

  /**
   * A new writer can briefly meet an older API during a rolling deployment.
   * That response has no top-level addresses; dereferencing `callbackUrl`
   * used to crash the whole React tree and leave Settings completely blank.
   * Keep the rest of the application alive and make the temporary mismatch
   * explicit. The current API always supplies this field.
   */
  if (!state.addresses) {
    return (
      <section className="settings__card" data-sso-card>
        <h2>Your own directory</h2>
        <p className="card__failed" role="alert">
          <Icon name="alert" size={13} /> Single sign-on setup is temporarily unavailable. Nothing
          has been changed. Reload this page and try again.
        </p>
      </section>
    )
  }

  const on = state.connection

  return (
    <section className="settings__card" data-sso-card>
      <h2>Your own directory</h2>
      <p className="home__quiet">
        People at your company sign in through Entra, Okta, Auth0, Google Workspace, Keycloak — any
        directory that speaks OpenID Connect or SAML. It is one door, not one integration per
        vendor: what changes between them is the wording in their console, not what you do here.
      </p>
      {/*
        The sentence this replaces said "taking somebody out of your directory
        takes them out of here", and it is not true. Measured on 2026-08-31:
        removing somebody from a directory shuts the door and leaves the
        membership, so they still appear in People, still count against the
        seats, and still receive the weekly letter. `sso.ts` has always said so
        in its own comment; the screen said the opposite to the person deciding
        whether to buy it. Deprovisioning is SCIM, and SCIM is planned rather
        than built — see `docs/SCIM.md`.
      */}
      <p className="home__quiet">
        Signing in comes from your directory; removing somebody from it stops them signing in and
        does not remove them here. Take them out under People as well.
      </p>

      {!state.configurable && (
        <p className="screen__warn">
          <Icon name="info" size={13} /> This deployment has no key to encrypt a client secret with,
          so a directory cannot be connected here. Your operator sets one.
        </p>
      )}

      {/*
        The three addresses, ABOVE everything and whether or not a directory is
        connected.

        They used to sit inside the connected branch, which is the wrong way
        round: every console asks for these while you are creating the
        application, so the workspace that needs them is the one that has
        nothing saved yet. The owner met exactly that and read his own callback
        address out of a running process on a production box, because the
        handle is a hash and nothing printed it.

        Each is named the way THIS product means it, with the words the three
        consoles use underneath — an administrator is looking for the label in
        front of them, and Entra, Okta and Auth0 each call the same address
        something different.

        Selectable text rather than a copy button: copying is the whole job,
        and a clipboard button that fails silently is worse than text somebody
        can select.
      */}
      <div className="label">Give your directory these</div>
      <dl className="settings__facts" data-sso-addresses>
        <dt>Callback address</dt>
        <dd>
          <code data-sso-callback>{state.addresses.callbackUrl}</code>
          <span className="settings__alias">
            OpenID Connect. Entra calls it a redirect URI, Okta a sign-in redirect URI, Auth0 an
            allowed callback URL.
          </span>
        </dd>
        <dt>Entity ID</dt>
        <dd>
          <code>{state.addresses.entityId}</code>
          <span className="settings__alias">
            SAML. Also called the audience, or the service provider identifier. The same address
            serves our metadata, if the console offers to fetch it.
          </span>
        </dd>
        <dt>Assertion consumer</dt>
        <dd>
          <code>{state.addresses.acsUrl}</code>
          <span className="settings__alias">SAML. Entra calls it the reply URL.</span>
        </dd>
      </dl>

      {on ? (
        <>
          <dl className="settings__facts">
            <dt>Signing in through</dt>
            <dd>
              {on.label} <span className="badge badge--quiet">{on.protocol}</span>
            </dd>
            <dt>Their identifier</dt>
            <dd>
              <code>{on.issuer}</code>
            </dd>
            <dt>New people arrive as</dt>
            <dd>{ROLE_LABELS[on.defaultRole]}</dd>
          </dl>
          {on.problem && (
            <p className="card__failed">
              <Icon name="alert" size={13} /> {on.problem}
            </p>
          )}
          {mayChange && (
            <div className="settings__row">
              <button
                className="btn btn--danger btn--sm"
                disabled={busy !== null}
                onClick={() => setDisconnecting(true)}
              >
                Disconnect
              </button>
            </div>
          )}
          {disconnecting && (
            <Confirm
              says="People at your proved domains go back to signing in with a password, and anybody who has never set one will have to be sent a sign-in link. Nobody is removed and nothing they wrote is touched."
              does="Disconnect it"
              cancel="Keep it"
              busy={busy !== null}
              onYes={() => {
                setDisconnecting(false)
                void act('disconnect', () => sso.disconnect(), 'The directory is disconnected.')
              }}
              onNo={() => setDisconnecting(false)}
            />
          )}
        </>
      ) : (
        mayChange &&
        state.configurable && (
          <>
            {/*
              Pasted, not typed field by field. Every directory hands its
              administrator a metadata document; asking them to find the entity
              id inside it and copy it across is asking them to do what a
              parser does, and to get it wrong once.
            */}
            <label className="field">
              <span className="label">Paste the metadata your directory gives you</span>
              <textarea
                className="textarea"
                rows={4}
                value={metadata}
                placeholder="<EntityDescriptor …>"
                onChange={(e) => setMetadata(e.target.value)}
              />
            </label>
            <div className="settings__row">
              <button
                className="btn"
                disabled={busy !== null || metadata.trim() === ''}
                onClick={() =>
                  void act(
                    'read',
                    async () => {
                      const read = await sso.readMetadata(metadata)
                      setDraft((was) => ({
                        ...was,
                        protocol: 'saml',
                        issuer: read.entityId,
                        ssoUrl: read.ssoUrl,
                        certificates: read.certificates,
                      }))
                    },
                    'Read it. Check the three fields below, then connect.',
                  )
                }
              >
                {busy === 'read' ? <Icon name="spinner" size={13} /> : null}
                Read it
              </button>
            </div>

            <label className="field">
              <span className="label">What to call it on the sign-in screen</span>
              <input
                className="input"
                value={draft.label ?? ''}
                placeholder="Acme single sign-on"
                onChange={(e) => setDraft((was) => ({ ...was, label: e.target.value }))}
              />
            </label>
            <label className="field">
              <span className="label">Their identifier</span>
              <input
                className="input mono"
                value={draft.issuer}
                onChange={(e) => setDraft((was) => ({ ...was, issuer: e.target.value }))}
              />
            </label>
            <label className="field">
              <span className="label">Where to send people to sign in</span>
              <input
                className="input mono"
                value={draft.ssoUrl ?? ''}
                onChange={(e) => setDraft((was) => ({ ...was, ssoUrl: e.target.value }))}
              />
            </label>
            <label className="field">
              <span className="label">New people arrive as</span>
              <select
                className="select"
                value={draft.defaultRole ?? 'editor'}
                onChange={(e) =>
                  setDraft((was) => ({
                    ...was,
                    defaultRole: e.target.value as 'admin' | 'editor' | 'viewer',
                  }))
                }
              >
                {(['admin', 'editor', 'viewer'] as const).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <div className="settings__row">
              <button
                className="btn btn--tinted"
                data-sso-connect
                disabled={busy !== null || draft.issuer.trim() === ''}
                onClick={() =>
                  void act(
                    'configure',
                    () => sso.configure(draft),
                    'Connected. Prove an email domain below, and people at it are sent to your directory.',
                  )
                }
              >
                {busy === 'configure' ? <Icon name="spinner" size={13} /> : null}
                Connect it
              </button>
            </div>
          </>
        )
      )}

      {/*
        The domains, which are what actually routes anybody: a connection with
        no proved domain signs nobody in, and that is the half a customer
        forgets. Same shape as a site's own hostname — claim it, publish the
        record, press the check — because it is the same problem.
      */}
      <div className="label">Email domains this directory signs in</div>
      {state.domains.length === 0 ? (
        <p className="nothing">
          None yet. Until one is proved, everybody here signs in with a password.
        </p>
      ) : (
        <ul className="people">
          {state.domains.map((d) => (
            <li className="people__row" key={d.domain} data-sso-domain={d.domain}>
              <span className="people__who">
                <span className="people__name">{d.domain}</span>
                <span className="people__email">
                  {d.state === 'verified'
                    ? 'Proved — people at this domain are sent to your directory.'
                    : d.record
                      ? `Publish ${d.record.type} ${d.record.name} = ${d.record.value}, then check it.`
                      : 'Waiting to be proved.'}
                  {d.problem ? ` ${d.problem}` : ''}
                </span>
              </span>
              <span className={`badge${d.state === 'verified' ? '' : ' badge--warn'}`}>
                {d.state === 'verified' ? 'proved' : 'pending'}
              </span>
              {mayChange && d.state !== 'verified' && (
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={busy !== null}
                  onClick={() =>
                    void act(
                      `verify:${d.domain}`,
                      () => sso.verifyDomain(d.domain),
                      'Looked for the record.',
                    )
                  }
                >
                  {busy === `verify:${d.domain}` ? <Icon name="spinner" size={13} /> : null}
                  Check the record
                </button>
              )}
              {mayChange && (
                <button
                  className="btn btn--danger btn--sm"
                  disabled={busy !== null}
                  onClick={() => setDropping(d.domain)}
                >
                  Remove
                </button>
              )}
              {dropping === d.domain && (
                <Confirm
                  says={`Nobody at ${d.domain} is sent to your directory afterwards. They keep their account and sign in with a password.`}
                  does="Remove it"
                  cancel="Keep it"
                  busy={busy !== null}
                  onYes={() => {
                    setDropping(null)
                    void act(
                      `drop:${d.domain}`,
                      () => sso.removeDomain(d.domain),
                      'That domain is no longer routed here.',
                    )
                  }}
                  onNo={() => setDropping(null)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {mayChange && (
        <div className="settings__row">
          <label className="field">
            <span className="label">Add an email domain</span>
            <input
              className="input"
              data-sso-domain-input
              value={domain}
              placeholder="acme.com"
              onChange={(e) => setDomain(e.target.value)}
            />
          </label>
          <button
            className="btn"
            disabled={busy !== null || domain.trim() === ''}
            onClick={() =>
              void act(
                'add-domain',
                async () => {
                  await sso.addDomain(domain.trim())
                  setDomain('')
                },
                'Claimed. Publish the record beside it, then press Check the record.',
              )
            }
          >
            {busy === 'add-domain' ? <Icon name="spinner" size={13} /> : null}
            Claim it
          </button>
        </div>
      )}

      {error && (
        <p className="card__failed" role="status">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {note && !error && (
        <p className="card__done" role="status">
          <Icon name="check" size={13} /> {note}
        </p>
      )}
    </section>
  )
}
